// ============================================================================
// 📁 FILE: src/modules/ticket/components/detail/StatusBlock.js
// 🎨 Updated On: 2025-12-06
// ============================================================================

import React, { useEffect, useRef, useState } from "react";
import { Tag, Button, Select } from "antd";
import WorkflowControls from "../WorkflowControls";

const { Option } = Select;

export default function StatusBlock({ ticket, onUpdated }) {
  // --------------------------------------------------------------------------
  // 🔧 1. SAFE HOOKS DECLARATION (must run on every render)
  // --------------------------------------------------------------------------

  const status = (ticket?.status || "").toUpperCase();

  const [selectedRule, setSelectedRule] = useState(null);
  const [nextStatuses, setNextStatuses] = useState([]);

  const workflowRef = useRef();

  // --------------------------------------------------------------------------
  // 🔄 Load workflow transitions when ticket changes
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (ticket && workflowRef.current?.loadTransitions) {
      workflowRef.current.loadTransitions(ticket.id).then((rules) => {
        setNextStatuses(rules || []);
      });
    }
  }, [ticket?.id]);

  // --------------------------------------------------------------------------
  // 🔄 Reset dropdown when status changes
  // --------------------------------------------------------------------------
  useEffect(() => {
    setSelectedRule(null);
  }, [ticket?.status]);

  // --------------------------------------------------------------------------
  // ⛔ CONDITIONAL RETURN (ONLY AFTER HOOKS)
  // --------------------------------------------------------------------------
  if (!ticket) return null;

  // Badge colors
  const statusColor = {
    OPEN: "blue",
    IN_PROGRESS: "orange",
    RESOLVED: "green",
    CLOSED: "red",
  }[status] || "default";

  // --------------------------------------------------------------------------
  // 🎨 RENDER
  // --------------------------------------------------------------------------
  return (
    <div
      style={{
        background: "#fff",
        padding: "20px 24px",
        borderRadius: "14px",
        marginTop: "18px",
        marginBottom: "22px",
        boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
        border: "1px solid #eee",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {/* STATUS BADGE */}
        <Tag
          color={statusColor}
          style={{
            padding: "6px 14px",
            fontSize: "14px",
            borderRadius: "8px",
            fontWeight: 600,
          }}
        >
          STATUS: {status.replace("_", " ")}
        </Tag>

        {/* WORKFLOW DROPDOWN */}
        <Select
          placeholder="Select next status"
          style={{ width: 180 }}
          size="middle"
          value={selectedRule?.to_status || undefined}
          onChange={(value) => {
            const rule = nextStatuses.find((r) => r.to_status === value);
            setSelectedRule(rule);
          }}
        >
          {nextStatuses.map((rule) => (
            <Option key={rule.id} value={rule.to_status}>
              {rule.to_status.replace(/_/g, " ")}
            </Option>
          ))}
        </Select>

        {/* UPDATE STATUS BUTTON */}
        <Button
          disabled={!selectedRule}
          onClick={() => {
            workflowRef.current?.triggerStatusChange?.({
              ticketId: ticket.id,
              rule: selectedRule,
              onUpdated,
            });
          }}
          style={{
            marginLeft: "auto",
            background: "linear-gradient(90deg,#6A00F4,#D100B8)",
            color: "#fff",
            border: "none",
            fontWeight: 600,
            padding: "6px 20px",
            borderRadius: "8px",
          }}
        >
          Update Status
        </Button>
      </div>

      {/* Hidden workflow engine */}
      <WorkflowControls ref={workflowRef} ticketId={ticket.id} />
    </div>
  );
}
